import React from 'react';
import PropTypes from 'prop-types';

import Download from '@decisiv/iconix/lib/components/Download';

import Button from '@decisiv/ui-components/lib/components/Button';
import Message from '@decisiv/ui-components/lib/components/Message';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';

import allowDefaultActionInModal from '../../../shared/modalClick';
import { SUMMARY_PANEL } from './copy';
import { StepBody, SummaryLines, SummaryRow } from './styles';

/**
 * Step 2: what the import did (Figma 787:76985).
 *
 * Three shapes, chosen by status rather than by counting here, so that the panel
 * and the server agree on what "partial" means:
 *
 *   success          one line, with a success Message
 *   partial_success  two lines, plain — the mockup gives this one no icon
 *   failure          one line, with a danger Message
 *
 * The text is Message's children rather than its `title`, because Message drops
 * the title at its default `small` size — which is the compact single-line row
 * the mockup draws.
 *
 * DOWNLOAD REPORT sits opposite the message in all three, because a successful
 * import still names the usernames the server derived, and that is the only
 * place the admin can read them.
 */
function SummaryPanel({ importRecord }) {
  const { failed, reportPath, status, succeeded, total } = importRecord;

  const message = () => {
    if (status === 'success') {
      return (
        <Message intent="success">
          {SUMMARY_PANEL.success(succeeded, total)}
        </Message>
      );
    }

    if (status === 'partial_success') {
      return (
        <SummaryLines>
          <P style={{ margin: 0 }}>
            {SUMMARY_PANEL.partialSucceeded(succeeded)}
          </P>
          <P style={{ margin: 0 }}>{SUMMARY_PANEL.partialFailed(failed)}</P>
        </SummaryLines>
      );
    }

    // A file nothing could be read from has no row count to quote, so it says so
    // rather than claiming "All 0 Users failed".
    const body = total > 0 ? SUMMARY_PANEL.failure(total) : SUMMARY_PANEL.empty;

    return <Message intent="danger">{body}</Message>;
  };

  return (
    <StepBody>
      <H3 style={{ margin: 0 }}>{SUMMARY_PANEL.title}</H3>
      <SummaryRow>
        {message()}
        {reportPath && (
          <Button
            download
            as="a"
            href={reportPath}
            icon={Download}
            kind="secondary"
            size="small"
            text={SUMMARY_PANEL.download}
            onClick={allowDefaultActionInModal}
          />
        )}
      </SummaryRow>
    </StepBody>
  );
}

SummaryPanel.propTypes = {
  importRecord: PropTypes.shape({
    failed: PropTypes.number,
    reportPath: PropTypes.string,
    status: PropTypes.string,
    succeeded: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
};

export default SummaryPanel;
